import { supabase } from './supabase'

export interface UserInfo {
  id: string
  nickname: string
  avatar: string
  avatar_color?: string
}

type AuthChangeListener = (user: UserInfo | null) => void

class AuthManager {
  private static instance: AuthManager
  private currentUser: UserInfo | null = null
  private listeners: Set<AuthChangeListener> = new Set()
  private isInitialized = false

  private constructor() {}

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager()
    }
    return AuthManager.instance
  }

  private loadUserFromStorage(): UserInfo | null {
    try {
      const userId = localStorage.getItem('avalon_user_id')
      const nickname = localStorage.getItem('avalon_user_nickname')
      const avatar = localStorage.getItem('avalon_user_avatar')
      const avatarColor = localStorage.getItem('avalon_user_avatar_color')

      if (userId && nickname) {
        return {
          id: userId,
          nickname,
          avatar: avatar || '🧙‍♂️',
          avatar_color: avatarColor || undefined,
        }
      }
      return null
    } catch {
      return null
    }
  }

  private saveUserToStorage(user: UserInfo): void {
    localStorage.setItem('avalon_user_id', user.id)
    localStorage.setItem('avalon_user_nickname', user.nickname)
    localStorage.setItem('avalon_user_avatar', user.avatar)
    if (user.avatar_color) {
      localStorage.setItem('avalon_user_avatar_color', user.avatar_color)
    }
  }

  private clearUserFromStorage(): void {
    localStorage.removeItem('avalon_user_id')
    localStorage.removeItem('avalon_user_nickname')
    localStorage.removeItem('avalon_user_avatar')
    localStorage.removeItem('avalon_user_avatar_color')
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentUser))
  }

  init(): void {
    if (this.isInitialized) return
    
    this.currentUser = this.loadUserFromStorage()
    this.isInitialized = true
    this.notifyListeners()
  }

  subscribe(listener: AuthChangeListener): () => void {
    this.listeners.add(listener)
    // Immediately call with current state
    listener(this.currentUser)
    
    return () => {
      this.listeners.delete(listener)
    }
  }

  getCurrentUser(): UserInfo | null {
    return this.currentUser
  }

  isLoggedIn(): boolean {
    return this.currentUser !== null
  }

  async login(nickname: string, avatar: string = '🧙‍♂️', avatarColor?: string): Promise<UserInfo> {
    try {
      const { data: userId, error: rpcError } = await supabase.rpc('get_or_create_game_user', {
        p_nickname: nickname.trim()
      })

      if (rpcError) throw rpcError

      const user: UserInfo = {
        id: userId as string,
        nickname: nickname.trim(),
        avatar,
        avatar_color: avatarColor,
      }

      this.currentUser = user
      this.saveUserToStorage(user)
      this.notifyListeners()

      return user
    } catch (err) {
      console.error('Login error:', err)
      throw err
    }
  }

  updateUser(updates: Partial<Pick<UserInfo, 'nickname' | 'avatar' | 'avatar_color'>>): void {
    if (!this.currentUser) return
    
    this.currentUser = { ...this.currentUser, ...updates }
    this.saveUserToStorage(this.currentUser)
    this.notifyListeners()
  }

  logout(): void {
    this.currentUser = null
    this.clearUserFromStorage()
    this.notifyListeners()
  }
}

// Export singleton instance and convenience functions
const authManager = AuthManager.getInstance()

export const auth = {
  init: () => authManager.init(),
  subscribe: (listener: AuthChangeListener) => authManager.subscribe(listener),
  getCurrentUser: () => authManager.getCurrentUser(),
  isLoggedIn: () => authManager.isLoggedIn(),
  login: (nickname: string, avatar?: string, avatarColor?: string) => authManager.login(nickname, avatar, avatarColor),
  logout: () => authManager.logout(),
  updateUser: (updates: Partial<Pick<UserInfo, 'nickname' | 'avatar' | 'avatar_color'>>) => authManager.updateUser(updates),
}

